import RevealVideo from '@/components/reveal/RevealVideo';
import CardSelectView from '@/components/reveal/CardSelectView';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { dailyRevealApi } from '@/api/dailyRevealApi';
import {
  useRevealPack,
  usePickCard,
  useDayFixtures,
  findFixtureForCard,
  dailyRevealKeys,
} from '@/hooks/useDailyReveal';
import { mapApiCardToRevealCard, getStoredGameContext, setStoredGameContext } from '@/types/reveal';
import type { RevealCard, UserCardWithDetails } from '@/types/reveal';
import { toast } from 'sonner';
import { AdSlot } from '@low6-pickem/ui';
import { useAdPlacements } from '@/hooks/useAdPlacements';

import { resolveTenantFromUrl } from '@low6-pickem/frontend-shared';
import { DailyRevealStyleProvider, useDailyRevealStyle } from '@/contexts/DailyRevealStyleContext';
import { useGameDetails } from '@/hooks/useDailyReveal';
import { isPropBasedGame, isPropPickLocked } from '@/utils/propBasedDailyReveal';
import { buildContestPath, type RevealLocationState } from '@/utils/revealNavigation';

const TENANT_SLUG = resolveTenantFromUrl();

export default function RevealView() {
  const location = useLocation();
  const navigate = useNavigate();
  const locState = location.state as RevealLocationState | null;

  const dayId = locState?.dayId ?? getStoredGameContext()?.dayId ?? null;
  const gameId = locState?.gameId ?? getStoredGameContext()?.gameId ?? null;
  const { data: gameData, isError: detailsIsError } = useGameDetails(gameId);

  // Where closing or completing the reveal goes back to. Prefer the path the entry
  // view handed over; fall back to the contest we resolved (from router state or the
  // stored context, so a refresh on /reveal still returns to the right week).
  const returnTo = locState?.returnTo ?? buildContestPath(gameId, location.search);

  // DKPLAT-813 — defer branded assets until the contest sport is known, so an NFL contest never
  // paints the Big League Draw logo / ball-park background first. The deferral needs a TERMINAL
  // escape hatch or an unhappy path would sit on an empty header/background forever:
  //  - no contest id at all: the details query is `enabled: !!gameId`, so it never resolves;
  //  - the details request errored (404 on a stale id, network failure after retries): the sport
  //    will never arrive, so fall through to today's defaults, which is what `main` showed.
  // Both signals are terminal by construction. Do NOT gate on `isLoading`/`isPending` instead: a
  // disabled query reports `isLoading: false` with no data, which is the DKPLAT-446 trap.
  const contestSport = gameData?.game?.sport ?? null;
  const isSportResolved = contestSport != null || !gameId || detailsIsError;

  return (
    <DailyRevealStyleProvider
      styleConfig={gameData?.game?.styleConfig}
      sport={contestSport}
      isSportResolved={isSportResolved}
      leagueIp={gameData?.game?.leagueIp === true}
    >
      <RevealViewContent
        dayId={dayId}
        gameId={gameId}
        returnTo={returnTo}
        skipAnimation={locState?.skipAnimation ?? false}
        navigate={navigate}
      />
    </DailyRevealStyleProvider>
  );
}

function RevealViewContent({
  dayId,
  gameId,
  returnTo,
  skipAnimation,
  navigate,
}: {
  dayId: string | null;
  gameId: string | null;
  returnTo: string;
  skipAnimation: boolean;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const [showCardSelect, setShowCardSelect] = useState(skipAnimation);
  const [showVideo, setShowVideo] = useState(!skipAnimation);
  const [revealedCards, setRevealedCards] = useState<RevealCard[]>([]);
  const [entryId, setEntryId] = useState<string | null>(null);
  const [revealError, setRevealError] = useState<string | null>(null);
  const [leagueIpEnabled, setLeagueIpEnabled] = useState(false);

  const queryClient = useQueryClient();
  const revealMutation = useRevealPack();
  const pickMutation = usePickCard();
  const { data: fixturesData } = useDayFixtures(dayId, { refetchInterval: 30_000 });
  const fixtures = fixturesData?.fixtures ?? [];
  const { data: gameData } = useGameDetails(gameId);
  const isPropBased = isPropBasedGame(gameData?.game);
  const currentDay = gameData?.days.find(d => d.id === dayId) ?? null;

  // Check if the currently selected card's pick is locked (prop lock or fixture started)
  const currentPickLocked = (() => {
    const selectedCard = revealedCards.find(c => c.isSelected);
    if (!selectedCard) return false;
    const fixture = findFixtureForCard(selectedCard.teamExternalId, fixtures, selectedCard.team);
    if (isPropBased) {
      return isPropPickLocked(selectedCard.prop, Date.now(), fixture);
    }
    if (!fixture) return false;
    return (
      fixture.status === 'live' || fixture.status === 'finished' || fixture.status === 'cancelled'
    );
  })();

  const { data: adPlacements } = useAdPlacements(TENANT_SLUG);
  const { revealBackgroundUrl, mainHeaderLogoUrl, isSportResolved } = useDailyRevealStyle();
  const contestSport = String(gameData?.game?.sport ?? '')
    .trim()
    .toUpperCase();
  const revealBackgroundStyle = useMemo(() => {
    // MLB / NFL art ships with the Figma darkening baked in, so this gradient still sits on
    // top. The NBA court export does not — applying the same overlay makes Full Court Draw
    // look almost black. Skip it for NBA only; leave every other sport untouched.
    const useDarkOverlay = contestSport !== 'NBA';
    const gradient =
      'radial-gradient(50.02% 70.83% at 50% 50%, rgba(18, 18, 18, 0.60) 0%, #121212 100%)';
    // Until the sport is known the only background we could name is the wrong league's, so drop
    // the image layer entirely and keep the gradient over the #121212 page background. Never
    // interpolate an unresolved value into url() — that requests a literal `null` (DKPLAT-813).
    if (!isSportResolved) {
      return {
        backgroundImage: gradient,
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
      };
    }
    if (!useDarkOverlay) {
      return {
        backgroundImage: `url('${revealBackgroundUrl}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
      };
    }
    return {
      backgroundImage: `${gradient}, url('${revealBackgroundUrl}')`,
      backgroundSize: 'cover, cover',
      backgroundPosition: 'center center, center center',
      backgroundRepeat: 'no-repeat, no-repeat',
    };
  }, [revealBackgroundUrl, isSportResolved, contestSport]);
  const stickyFooterAd =
    adPlacements?.find(p => p.placementKey === 'DAILY_REVEAL_STICKY_FOOTER' && p.enabled) ?? null;
  const overLogoRevealAd =
    adPlacements?.find(p => p.placementKey === 'DAILY_REVEAL_OVER_LOGO_REVEAL' && p.enabled) ??
    null;

  const doReveal = useCallback(async () => {
    if (!dayId) {
      setRevealError('No day available for reveal');
      return;
    }

    try {
      const resp = await revealMutation.mutateAsync(dayId);
      setEntryId(resp.entry.id);

      if (gameId && dayId) {
        setStoredGameContext({ gameId, dayId, entryId: resp.entry.id });
      }

      let leagueIpFlag = false;
      if (gameId) {
        try {
          const gd = await queryClient.fetchQuery({
            queryKey: dailyRevealKeys.game(gameId),
            queryFn: () => dailyRevealApi.getGame(gameId),
          });
          leagueIpFlag = gd.game.leagueIp === true;
        } catch {
          leagueIpFlag = false;
        }
      }

      const cards = resp.cards.map((uc: UserCardWithDetails) =>
        mapApiCardToRevealCard(uc, leagueIpFlag)
      );
      setLeagueIpEnabled(leagueIpFlag);
      setRevealedCards(cards);
    } catch (err: unknown) {
      const message = getErrorMessage(err);
      setRevealError(message);
      toast.error(message);
    }
  }, [dayId, gameId, revealMutation, queryClient]);

  useEffect(() => {
    if (dayId && revealedCards.length === 0 && !revealMutation.isPending && !revealError) {
      doReveal();
    }
  }, [dayId]);

  useEffect(() => {
    if (revealError && showVideo) {
      setShowVideo(false);
    }
  }, [revealError, showVideo]);

  const handleVideoComplete = () => {
    setShowVideo(false);
    setShowCardSelect(true);
  };

  const handleCardPicked = async (card: RevealCard) => {
    if (!dayId || !entryId) {
      toast.error('Something went wrong. Please go back and try again.');
      throw new Error('Missing day or entry');
    }

    try {
      await pickMutation.mutateAsync({ dayId, cardId: card.cardId });
    } catch (err: unknown) {
      toast.error(getErrorMessage(err));
      throw err;
    }
  };

  // Wait for game details when we have a gameId so NFL and NBA get their own clip
  // instead of briefly starting Big League Draw.
  const revealVideoSportReady = !gameId || gameData != null;
  const shouldShowRevealVideo = showVideo && revealVideoSportReady;

  return (
    <main className="h-dvh min-h-[635px] bg-[#121212] text-white flex flex-col">
      <div className="w-full md:max-w-[500px] md:mx-auto md:shadow-2xl flex-1 h-full">
        <div className="w-full h-full bg-[#121212]">
          <section className="relative mx-auto w-[375px] max-w-full h-full min-h-[635px] overflow-hidden">
            <div className="absolute inset-0" style={revealBackgroundStyle} />

            {shouldShowRevealVideo && (
              <>
                <RevealVideo onComplete={handleVideoComplete} sport={gameData?.game?.sport} />
                {overLogoRevealAd && (
                  <div className="absolute left-0 top-0 z-10 h-0 w-0 overflow-hidden">
                    <AdSlot
                      divId="daily-reveal-over-logo-reveal"
                      adPlacement={overLogoRevealAd}
                      placement="over-logo-reveal-animation"
                      className="rounded-lg overflow-hidden"
                    />
                  </div>
                )}
              </>
            )}

            <div className="relative z-20 flex h-full flex-col items-center px-6 pt-5">
              {showCardSelect && (
                <button
                  type="button"
                  onClick={() => navigate(returnTo, { state: { dayId, gameId } })}
                  aria-label="Close and return to home"
                  className="absolute right-4 top-5 z-30 flex h-10 w-10 items-center justify-center rounded-full text-white/80 transition-colors hover:bg-white/10 hover:text-white"
                >
                  <X size={24} strokeWidth={2.5} />
                </button>
              )}
              {!showVideo &&
                (isSportResolved ? (
                  <img
                    src={mainHeaderLogoUrl}
                    alt="Daily Reveal"
                    className="h-[96px] w-auto max-w-[280px] object-contain"
                  />
                ) : (
                  // Reserve the logo box while the sport is unknown (DKPLAT-813).
                  <div className="h-[96px] w-auto max-w-[280px]" aria-hidden="true" />
                ))}

              {revealError && (
                <div className="mt-8 rounded-lg bg-red-900/40 border border-red-600/50 px-4 py-3 text-center">
                  <p className="text-[13px] text-red-300">{revealError}</p>
                  <button
                    type="button"
                    onClick={() => {
                      setRevealError(null);
                      doReveal();
                    }}
                    className="mt-2 text-[12px] font-semibold uppercase text-[#53d337] hover:underline"
                  >
                    Retry
                  </button>
                </div>
              )}

              {!showCardSelect && !showVideo ? (
                <>
                  <p className="daily-reveal-heading mt-[19px] text-[13px] uppercase tracking-[0.2px]">
                    {new Date().toLocaleDateString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </p>
                  <h1 className="daily-reveal-heading mt-[6px] text-[24.611px] uppercase leading-none tracking-[0.2px]">
                    Your Pick
                  </h1>
                  {revealMutation.isPending ? (
                    <p className="mt-[16px] text-center text-[12px] font-medium text-[#8a8a8a]">
                      Revealing your cards...
                    </p>
                  ) : null}
                  {stickyFooterAd && (
                    <AdSlot
                      divId="daily-reveal-sticky-footer"
                      adPlacement={stickyFooterAd}
                      placement="sticky-footer"
                      className="mt-auto mb-8 h-[90px] w-[321px] max-w-full"
                    />
                  )}
                </>
              ) : showCardSelect ? (
                <CardSelectView
                  cards={revealedCards}
                  dayId={dayId ?? ''}
                  gameId={gameId}
                  returnTo={returnTo}
                  fixtures={fixtures}
                  currentPickLocked={currentPickLocked}
                  onCardPicked={handleCardPicked}
                  leagueIpEnabled={leagueIpEnabled}
                  isPropBased={isPropBased}
                  categoryName={currentDay?.categoryName}
                />
              ) : null}
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}

function getErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const resp = (err as { response?: { data?: { error?: string }; status?: number } }).response;
    if (resp?.data?.error) return resp.data.error;
    if (resp?.status === 401) return 'Please log in to play.';
    if (resp?.status === 409) return 'This day is no longer available.';
    if (resp?.status === 503) return 'Cards are temporarily unavailable. Please try again.';
  }
  if (err instanceof Error) return err.message;
  return 'Something went wrong. Please try again.';
}
